import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Item, ItemCreate, ItemUpdate } from '@/app/types/item';
import { Category } from '@/app/types/category';
import { Unit } from '@/app/types/unit';
import { Godown } from '@/app/types/inventory';
import { TaxRate } from '@/app/types/taxRate';
import { AccountResponse } from '@/app/types/account';
import { Loader2 } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SimpleAccountSelector } from '@/app/components/common/SimpleAccountSelector';
import { OrganizationSettingResponse } from '@/app/types/organizationSettings';
import { AdditionalFields } from './AdditionalFields';

// Form schema
const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().nullable().optional(),
  hsn_code: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  item_type: z.enum(['product', 'service']),
  is_active: z.boolean().default(true),
  category_id: z.number().nullable().optional(),
  base_unit_id: z.number().min(1, 'Base unit is required'),
  secondary_unit_id: z.number().nullable().optional(),
  conversion_factor: z.union([
    z.number().positive('Must be a positive number'),
    z.null(),
  ]).optional().nullable(),
  default_godown_id: z.number().nullable().optional(),
  min_stock_level: z.union([
    z.number().min(0, 'Cannot be negative'),
    z.null(),
  ]).optional().nullable(),
  max_stock_level: z.union([
    z.number().min(0, 'Cannot be negative'),
    z.null(),
  ]).optional().nullable(),
  reorder_level: z.union([
    z.number().min(0, 'Cannot be negative'),
    z.null(),
  ]).optional().nullable(),
  is_gst_applicable: z.boolean().default(false),
  tax_rate_id: z.number().nullable().optional(),
  purchase_account_id: z.number().nullable().optional(),
  sales_account_id: z.number().nullable().optional(),
  inventory_account_id: z.number().nullable().optional(),
  rate_decimal_places: z.union([
    z.number().min(0, 'Cannot be negative').max(4, 'Maximum 4 decimal places allowed'),
    z.null(),
  ]).optional().nullable(),
  additional_fields: z.record(z.any()).nullable().optional(),
}).superRefine((data, ctx) => {
  // If item type is product, inventory account is required
  if (data.item_type === 'product' && !data.inventory_account_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Inventory account is required for products',
      path: ['inventory_account_id'],
    });
  }
});

type FormValues = z.infer<typeof formSchema>;

interface ItemFormProps {
  item?: Item; // For editing existing item
  onSubmit: (data: ItemCreate | ItemUpdate) => void;
  isSubmitting: boolean;
  // Dropdown options
  categories: Category[];
  units: Unit[];
  godowns: Godown[];
  taxRates: TaxRate[];
  accounts?: AccountResponse[]; // Made optional since we'll use SimpleAccountSelector
  organizationSettings?: OrganizationSettingResponse;
}

export function ItemForm({
  item,
  onSubmit,
  isSubmitting,
  categories,
  units,
  godowns,
  taxRates,
  accounts,
  organizationSettings,
}: ItemFormProps) {

  // Initialize form with existing item data or defaults
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: item
      ? {
          ...item,
          // Convert string decimals to numbers if needed
          conversion_factor:
            item.conversion_factor !== null
              ? typeof item.conversion_factor === 'string'
                ? parseFloat(item.conversion_factor)
                : item.conversion_factor
              : null,
          min_stock_level:
            item.min_stock_level !== null
              ? typeof item.min_stock_level === 'string'
                ? parseFloat(item.min_stock_level)
                : item.min_stock_level
              : null,
          max_stock_level:
            item.max_stock_level !== null
              ? typeof item.max_stock_level === 'string'
                ? parseFloat(item.max_stock_level)
                : item.max_stock_level
              : null,
          reorder_level:
            item.reorder_level !== null
              ? typeof item.reorder_level === 'string'
                ? parseFloat(item.reorder_level)
                : item.reorder_level
              : null,
          rate_decimal_places:
            item.rate_decimal_places !== null && item.rate_decimal_places !== undefined
              ? typeof item.rate_decimal_places === 'string'
                ? parseInt(item.rate_decimal_places)
                : item.rate_decimal_places
              : 0,
          additional_fields: item.additional_fields || null,
        }
      : {
          name: '',
          code: null,
          hsn_code: null,
          description: null,
          item_type: 'product',
          is_active: true,
          category_id: null,
          base_unit_id: 0, // Will be updated by form selection
          secondary_unit_id: null,
          conversion_factor: null,
          default_godown_id: null,
          min_stock_level: null,
          max_stock_level: null,
          reorder_level: null,
          is_gst_applicable: false,
          tax_rate_id: null,
          purchase_account_id: organizationSettings?.default_purchase_account_id || null,
          sales_account_id: organizationSettings?.default_sales_account_id || null,
          inventory_account_id: null, // No default inventory account in organization settings
          rate_decimal_places: 0, // Default to 0 decimal places (whole numbers)
          additional_fields: null,
        },
  });

  // Reset form when item changes (for edit dialog)
  React.useEffect(() => {
    if (item) {
      form.reset({
        ...item,
        // Convert string decimals to numbers if needed
        conversion_factor:
          item.conversion_factor !== null
            ? typeof item.conversion_factor === 'string'
              ? parseFloat(item.conversion_factor)
              : item.conversion_factor
            : null,
        min_stock_level:
          item.min_stock_level !== null
            ? typeof item.min_stock_level === 'string'
              ? parseFloat(item.min_stock_level)
              : item.min_stock_level
            : null,
        max_stock_level:
          item.max_stock_level !== null
            ? typeof item.max_stock_level === 'string'
              ? parseFloat(item.max_stock_level)
              : item.max_stock_level
            : null,
        reorder_level:
          item.reorder_level !== null
            ? typeof item.reorder_level === 'string'
              ? parseFloat(item.reorder_level)
              : item.reorder_level
            : null,
        rate_decimal_places:
          item.rate_decimal_places !== null && item.rate_decimal_places !== undefined
            ? typeof item.rate_decimal_places === 'string'
              ? parseInt(item.rate_decimal_places)
              : item.rate_decimal_places
            : 0,
        additional_fields: item.additional_fields || null,
      });
    }
  }, [item, form]);

  // Watch form fields for conditional rendering
  const itemType = form.watch('item_type');
  const isGstApplicable = form.watch('is_gst_applicable');

  // Handle form submission
  const handleSubmit = (values: FormValues) => {
    onSubmit(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Basic Information */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Item name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Code</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Item code" 
                    {...field} 
                    value={field.value || ''} 
                    onChange={(e) => field.onChange(e.target.value || null)}
                  />
                </FormControl>
                <FormDescription>Unique identifier for this item</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="item_type"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>Item Type *</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex flex-row space-x-4"
                  >
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="product" />
                      </FormControl>
                      <FormLabel className="font-normal">Product</FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="service" />
                      </FormControl>
                      <FormLabel className="font-normal">Service</FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="hsn_code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>HSN Code</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="HSN code" 
                    {...field} 
                    value={field.value || ''} 
                    onChange={(e) => field.onChange(e.target.value || null)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="rate_decimal_places"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rate Decimal Places</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    max="4"
                    step="1"
                    placeholder="0"
                    {...field}
                    value={field.value === null ? '' : field.value}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        field.onChange(null);
                      } else {
                        const num = parseInt(value);
                        if (!isNaN(num) && num >= 0 && num <= 4) {
                          field.onChange(num);
                        }
                      }
                    }}
                  />
                </FormControl>
                <FormDescription>
                  Number of decimal places for rate (0-4). Default is 0 (whole numbers).
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="category_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(value !== "none" ? parseInt(value) : null)}
                  value={field.value?.toString() || 'none'}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-6">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Active</FormLabel>
                  <FormDescription>
                    Inactive items cannot be used in transactions
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        </div>

        {/* Description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Item description"
                  className="resize-none"
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Additional Information */}
        <FormField
          control={form.control}
          name="additional_fields"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <AdditionalFields
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Unit Information */}
          <FormField
            control={form.control}
            name="base_unit_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Base Unit *</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(parseInt(value))}
                  value={field.value?.toString() || undefined}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select base unit" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id.toString()}>
                        {unit.name} ({unit.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>Primary unit of measurement</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="secondary_unit_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Secondary Unit</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(value !== "none" ? parseInt(value) : null)}
                  value={field.value?.toString() || 'none'}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select secondary unit" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {units.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id.toString()}>
                        {unit.name} ({unit.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>Optional alternative unit</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {form.watch('secondary_unit_id') && (
            <FormField
              control={form.control}
              name="conversion_factor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Conversion Factor</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="1.0"
                      {...field}
                      value={field.value === null ? '' : field.value}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value === '' ? null : parseFloat(value));
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    How many base units equal one secondary unit
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        {/* Product-specific fields */}
        {itemType === 'product' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="default_godown_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Godown</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value !== "none" ? parseInt(value) : null)}
                      value={field.value?.toString() || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select godown" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {godowns.map((godown) => (
                          <SelectItem key={godown.id} value={godown.id.toString()}>
                            {godown.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="min_stock_level"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Min Stock Level</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0"
                        {...field}
                        value={field.value === null ? '' : field.value}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value === '' ? null : parseFloat(value));
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="max_stock_level"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Stock Level</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0"
                        {...field}
                        value={field.value === null ? '' : field.value}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value === '' ? null : parseFloat(value));
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="reorder_level"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reorder Level</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0"
                        {...field}
                        value={field.value === null ? '' : field.value}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value === '' ? null : parseFloat(value));
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="inventory_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Inventory Account *</FormLabel>
                  <FormControl>
                    <SimpleAccountSelector
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select inventory account"
                      filterAccountTypes={['Asset']}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>Asset account for inventory tracking (required for products)</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* GST Information */}
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="is_gst_applicable"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>GST Applicable</FormLabel>
                  <FormDescription>
                    Whether GST is applicable to this item
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          {isGstApplicable && (
            <FormField
              control={form.control}
              name="tax_rate_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax Rate</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value !== "none" ? parseInt(value) : null)}
                    value={field.value?.toString() || 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select tax rate" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {taxRates.map((taxRate) => (
                        <SelectItem key={taxRate.id} value={taxRate.id.toString()}>
                          {taxRate.name} ({taxRate.rate}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        {/* Accounting Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="purchase_account_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Purchase Account</FormLabel>
                <FormControl>
                  <SimpleAccountSelector
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select purchase account"
                    filterAccountTypes={['Expense']}
                    disabled={isSubmitting}
                  />
                </FormControl>
                <FormDescription>Expense account for purchases</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="sales_account_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Sales Account</FormLabel>
                <FormControl>
                  <SimpleAccountSelector
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select sales account"
                    filterAccountTypes={['Income']}
                    disabled={isSubmitting}
                  />
                </FormControl>
                <FormDescription>Income account for sales</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Submit Button */}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {item ? 'Update Item' : 'Create Item'}
        </Button>
      </form>
    </Form>
  );
} 